import { useTranslation } from 'react-i18next';

import {
  MENU_CATEGORY_KEYS,
  type MenuCategoryKey,
  type PermissionMap,
  type PermissionValue,
} from '../types/role.types';

const VALUES: readonly PermissionValue[] = ['NO_PERMISSION', 'READ', 'WRITE'];

interface PermissionMatrixTableProps {
  permissionMap: PermissionMap;
  readOnly: boolean;
  /**
   * Root's matrix is "전 카테고리 WRITE 고정 표시" (PRD 16 §3.5) — a display rule, not the stored value.
   * The server keeps Root's `CONTENT_LIB` as the `DELETE` special marker (PRD 21 §4.2.1), which the
   * three columns cannot express, so without this the row matched no radio and rendered blank, reading
   * as "Root has no content permission" (SHCT-630). Root is view-only, so nothing can persist from here.
   */
  forceWrite?: boolean;
  onChange?: (cat: MenuCategoryKey, val: PermissionValue) => void;
}

export function PermissionMatrixTable({
  permissionMap,
  readOnly,
  forceWrite = false,
  onChange,
}: PermissionMatrixTableProps) {
  const { t } = useTranslation('roles');

  return (
    <table className="table" style={{ margin: 0 }}>
      <thead>
        <tr style={{ background: 'var(--muted)' }}>
          <th>{t('matrix.header.category')}</th>
          <th style={{ width: 180, textAlign: 'center' }}>NO_PERMISSION</th>
          <th style={{ width: 140, textAlign: 'center' }}>READ</th>
          <th style={{ width: 140, textAlign: 'center' }}>WRITE</th>
        </tr>
      </thead>
      <tbody>
        {MENU_CATEGORY_KEYS.map((cat) => {
          const current = forceWrite ? 'WRITE' : permissionMap[cat];
          const rowName = `perm_${cat}`;
          return (
            <tr key={cat}>
              <td style={{ fontWeight: 500 }}>{t(`matrix.categoryLabels.${cat}`)}</td>
              {VALUES.map((value) => (
                <td key={value} style={{ textAlign: 'center' }}>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name={rowName}
                      value={value}
                      checked={current === value}
                      onChange={() => onChange?.(cat, value)}
                      disabled={readOnly}
                    />
                    <span
                      style={{
                        color:
                          current === value ? 'var(--primary)' : 'var(--muted-foreground)',
                      }}
                    >
                      {value}
                    </span>
                  </label>
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
